import { apiClient } from "./apiClient";
import { ApiEnvelope, getAuthHeaders } from "./apiHelpers";
import type { KnowledgeQualityResult } from "../KnowledgeQuality/knowledgeQualityTypes";
import type { ConsistencyResult } from "../Consistency/utilities/types";
import type { GovernanceResult } from "../Governance/governanceTypes";

/* Re-exports so importers of the old top-level module keep working. */
export { apiClient, getAPI } from "./apiClient";
export type { ApiEnvelope } from "./apiHelpers";
export { ApiEnvelopeError, getAuthHeaders } from "./apiHelpers";

/* Supporting Evidence types */

export interface SupportingEvidence {
  id: string;
  title: string;
  tags: string[];
  hasFile: boolean;
  fileName: string | null;
  fileSize: number | null;
  contentType: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface EvidenceLink {
  id: string;
  assessmentPlanId: string;
  statementId: string;
  supportingEvidenceId: string;
  evidenceName?: string;
  linkedBy: string;
  createdAt: string;
}

export interface EvidencePreview {
  url: string;
  previewable: boolean;
  fileName: string;
}

/* Governance Document types */

export type GovernanceDocumentType =
  | "POLICY"
  | "STANDARD"
  | "GUIDELINE"
  | "PROCEDURE";

export type DocumentLifecycleStatus =
  | "DRAFT"
  | "IN_REVIEW"
  | "APPROVED"
  | "PUBLISHED"
  | "REVIEW_OVERDUE"
  | "RETIRED";

export type AllowedAction =
  | "UPDATE"
  | "UPLOAD"
  | "DELETE_FILE"
  | "DELETE"
  | "SUBMIT"
  | "APPROVE"
  | "REJECT"
  | "NEW_VERSION"
  | "RETIRE";

export interface VersionInfo {
  versionNumber: number;
  fileName: string | null;
  contentType: string | null;
  fileSize: number | null;
  hasFile: boolean;
  createdAt: string;
  createdBy: string;
  createdByName?: string;
}

export interface GovernanceDocumentResponse {
  id: string;
  documentType: GovernanceDocumentType;
  title: string;
  description: string | null;
  owningOrgUnitId: string | null;
  owningOrgUnitName?: string | null;
  status: DocumentLifecycleStatus;
  currentVersion: number;
  requiresReview: boolean;
  reviewCycleDays: number | null;
  reviewAlertDays?: number | null;
  reviewDate: string | null;
  daysUntilReview: number | null;
  reviewDueSoon?: boolean;
  rejectionReason: string | null;
  rejectedAt: string | null;
  rejectedBy?: string | null;
  rejectedByName?: string | null;
  publishedAt: string | null;
  publishedBy: string | null;
  publishedByName?: string | null;
  retiredAt?: string | null;
  retiredBy?: string | null;
  retiredByName?: string | null;
  retirementReason?: string | null;
  currentVersionInfo: VersionInfo | null;
  versions: VersionInfo[];
  createdAt: string;
  createdBy?: string;
  createdByName?: string;
  updatedAt: string;
  updatedBy?: string;
  updatedByName?: string;
  allowedActions?: AllowedAction[];
}

export interface ReviewSummaryResponse {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  byType?: Record<string, { total: number; pending: number; approved: number; rejected: number }>;
}

export interface GovernanceDocumentSummaryResponse {
  id: string;
  documentType: GovernanceDocumentType;
  title: string;
  owningOrgUnitId: string | null;
  status: DocumentLifecycleStatus;
  currentVersion: number;
  versionCount: number;
  requiresReview: boolean;
  reviewDate: string | null;
  daysUntilReview: number | null;
  reviewDueSoon?: boolean;
  hasFile: boolean;
  createdAt: string;
  createdBy?: string;
  createdByName?: string;
  updatedAt: string;
  updatedBy?: string;
  updatedByName?: string;
}

export interface SupportingEvidenceResponse {
  id: string;
  title: string;
  description: string | null;
  tags: string[];
  fileName: string | null;
  contentType: string | null;
  fileSize: number | null;
  hasFile: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface HistoryEvent {
  id: string;
  tenantId?: string;
  documentId: string;
  organizationEntryId?: string;
  versionNumber: number;
  eventType: string;
  eventDescription: string;
  fromStatus?: string | null;
  toStatus?: string | null;
  eventData?: Record<string, unknown> | null;
  performedBy: string;
  performedByName?: string;
  performedAt: string;
}

/* Doc-Engine ingest types */

export interface DocEngineIngestUploadResponse {
  document_id: string;
  /** Backend can return `"duplicate"` when the file (by hash) already exists —
   *  the caller must abort the ingest pipeline and prompt the user to pick a
   *  different file instead of continuing with SSE / classification. */
  status?: string;
  message?: string;
  [key: string]: unknown;
}

/** Sentinel returned by the upload endpoint when the file's content-hash
 *  already exists for this org. Compared case-insensitively. */
export const DOC_INGEST_STATUS_DUPLICATE = "duplicate";

export type IngestStageKey =
  | "ingestion"
  | "structure"
  | "canonical_record"
  | "semantic_chunking"
  | "chunk_selection"
  | "validate"
  | "embed"
  | "hub"
  | (string & {});

export interface IngestStageEvent {
  stage: IngestStageKey;
  status: string;
  ms?: number;
  detail?: string;
  percentComplete?: number;
  finished?: boolean;
  error?: string;
}

export type DocEngineIngestEvent = Partial<IngestStageEvent> & {
  documentId?: string;
  tracked?: boolean;
  finished?: boolean;
  failed?: boolean;
  percentComplete?: number;
  elapsedMs?: number;
  filename?: string;
  stages?: IngestStageEvent[];
  error?: string;
  message?: string;
  [key: string]: unknown;
};

export interface DocEngineIngestSubscribeOptions {
  onEvent: (name: string | undefined, payload: DocEngineIngestEvent) => void;
  signal?: AbortSignal;
}

export interface DocEngineDocumentRecord {
  id: string;
  documentId: string;
  title: string;
  documentType: string;
  organization?: string;
  organizationId?: string;
  reviewCycle?: string;
  description?: string;
  documentHash?: string;
  status: string;
  current?: boolean;
  createdAt?: string;
  updatedAt?: string;
  filename?: string;
  /** Populated by the doc-engine once the document has been withdrawn. */
  retirementReason?: string | null;
}

export interface DocEngineListPagination {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface DocEngineDocumentListResponse {
  data?: DocEngineDocumentRecord[];
  pagination?: DocEngineListPagination;
}

export interface DocEngineListPayload {
  docStatus?: string;
  trashed?: boolean;
  keyword?: string;
  domainName?: string;
  documentType?: string;
  organizationId?: string;
}

export interface DomainDocumentCount {
  domainName: string;
  documentCount: number;
  fileNames: string[];
}

export interface DocEngineListResult {
  items: DocEngineDocumentRecord[];
  totalItems: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface SaveDocumentIngestPayload {
  documentId: string;
  title: string;
  documentType?: string;
  domainName?: string;
  organization?: string;
  organizationId?: string;
  reviewCycle?: string;
  description?: string;
  documentHash?: string;
  status: string;
}

/* Domain / Concept / Requirement classifier types */

export interface DomainCoverage {
  domainCode: string;
  domainName: string;
  domainPurpose: string;
  policyRequirements: number;
  policyRequirementsTotal?: number;
  sharePct?: number | null;
  secondaryReferences?: number;
  coverageStatus: string;
  confidence?: number | null;
  mappedClauseIds?: string[];
  secondaryClauseIds?: string[];
  coverageBasis?: string;
}

export interface DomainSummary {
  requirementsAnalyzed: number;
  requirementsMapped: number;
  requirementsUnmapped: number;
  totalDomainMappings: number;
  multiDomainRequirements: number;
  domainsByStatus?: Record<string, number>;
}

export interface DomainMapping {
  clause_id?: string;
  requirement_id?: string | null;
  source_text?: string;
  primary_domain?: { code?: string; name?: string; confidence?: number };
  secondary_domains?: Array<{
    code?: string;
    name?: string;
    confidence?: number;
    reason?: string;
  }>;
  reason?: string;
  control_intent?: string;
  evidence?: Record<string, unknown>;
  issues?: unknown[];
  [key: string]: unknown;
}

export interface DomainGap {
  scfDomain?: string;
  scfDomainName?: string;
  scfPurpose?: string;
  gapType?: string;
  gapSeverity?: string;
  explanation?: string;
  existingPolicyEvidence?: unknown[];
  missingArea?: string;
  confidence?: number | null;
  note?: string;
}

export interface DomainTaxonomy {
  name?: string;
  version?: string;
  source?: string;
  origin?: string;
  documentId?: string;
  documentRef?: string;
  domainCount?: number;
  taxonomyHash?: string;
  warnings?: string[];
}

export interface DomainClassifyResult {
  stopped?: boolean;
  note?: string;
  document?: { id?: string; ref?: string; version?: string | null };
  taxonomy?: DomainTaxonomy;
  summary?: DomainSummary;
  coverage?: DomainCoverage[];
  mappings?: DomainMapping[];
  unmapped?: unknown[];
  gaps?: DomainGap[];
}

export interface DomainRuleFinding {
  via?: string;
  page?: number;
  text?: string;
  number?: number;
  section?: string;
  chunk_id?: string;
}

export interface DomainRuleValidation {
  ruleName: string;
  findings: DomainRuleFinding[];
}

export interface ConceptFinding {
  name: string;
  page: number | null;
  group: string | null;
  source: string;
  section: string | null;
  concept_id: string;
  confidence: number;
  description: string;
  occurrences: number;
  normalized_name: string;
  group_description: string;
  parent_concept_id: string | null;
  [key: string]: unknown;
}

/** One node in the concept-tree grouping (metrics.tree[]). */
export interface ConceptTreeGroup {
  group: string;
  group_description?: string;
  concept_count?: number;
  concepts?: ConceptFinding[];
}

export interface ConceptMetrics {
  tree?: ConceptTreeGroup[];
  total?: number;
  groups?: number;
  llm_pass?: string;
  by_source?: Record<string, number>;
  ungrouped?: number;
  defined_terms?: number;
  grouping_pass?: string;
  grouping_method?: string;
  recurring_terms?: number;
  tree_clause_text?: string;
  findings_returned?: number;
  findings_truncated?: number;
  [key: string]: unknown;
}

export interface ConceptClassifyResult {
  id?: string;
  documentId?: string;
  layer?: string;
  status?: string;
  summary?: string;
  count?: number;
  metrics?: ConceptMetrics;
  findings?: ConceptFinding[];
  [key: string]: unknown;
}

/** One normative obligation extracted from a document. */
export interface RequirementFinding {
  id?: string;
  requirement_id?: string;
  text?: string;
  modal?: string;
  actor?: string;
  action?: string;
  object?: string;
  constraints?: string | string[];
  section?: string;
  page?: number | null;
  strength?: string;
  decomposed?: boolean;
  flagged?: boolean;
  confidence?: number;
  clause_id?: string;
  [key: string]: unknown;
}

export interface RequirementActorRollup {
  actor: string;
  count: number;
}

export interface RequirementSectionRollup {
  section: string;
  count: number;
}

export interface RequirementMetrics {
  total?: number;
  fully_decomposed?: number;
  not_decomposed?: number;
  clauses_seen?: number;
  clauses_without_modal?: number;
  by_modal?: Record<string, number>;
  by_actor?: RequirementActorRollup[];
  by_section?: RequirementSectionRollup[];
  [key: string]: unknown;
}

export interface RequirementClassifyResult {
  id?: string;
  documentId?: string;
  layer?: string;
  status?: string;
  summary?: string;
  count?: number;
  metrics?: RequirementMetrics;
  findings?: RequirementFinding[];
  [key: string]: unknown;
}

/* Validation types */

export type ValidateRuleStatus = "PASS" | "FAIL" | "WARN" | "SKIPPED";
export type ValidateRuleSeverity = "ERROR" | "WARN";

export interface ValidateRuleResult {
  ruleId: string;
  name: string;
  severity: ValidateRuleSeverity;
  status: ValidateRuleStatus;
  evidence?: string;
  remediation?: string;
  category: string;
  skipReason?: string;
}

export interface ValidateFileSummary {
  pass: number;
  fail: number;
  warn: number;
  skipped: number;
  total: number;
}

export interface SevenLayerValidationLayer {
  key: string;
  label: string;
  status: string;
  passed: boolean;
  percentage: number;
  formattedPercentage?: string;
  score: number;
  rulesChecked: number;
  rulesPassed: number;
  hint?: string;
}

export interface SevenLayerValidation {
  totalLayers: number;
  clearedLayers: number;
  status: string;
  crossCuttingRules?: number;
  layers?: SevenLayerValidationLayer[];
}

interface ProcessedDocumentInfo {
  id?: string;
  title?: string;
  documentType?: string;
  fileName?: string;
  fileSize?: number;
  formattedFileSize?: string;
  mimeType?: string;
  owningOrgUnitId?: string;
  owningOrgUnitName?: string;
  version?: number;
  status?: string;
}

export interface ProcessingJobInfo {
  jobId?: string;
  phase?: string;
  status?: string;
  durationMs?: number;
  formattedDuration?: string;
  startedAt?: string;
  completedAt?: string;
  currentStage?: string;
  progressPercent?: number;
}

interface ProcessingPipelineStage {
  key: string;
  label?: string;
  status?: string;
  durationMs?: number;
  hint?: string;
}

interface LiveYieldMetric {
  count?: number;
  score?: number;
  percentage?: number;
  failedCount?: number;
  warningCount?: number;
  totalCount?: number;
  label?: string;
  status?: string;
}

interface LiveYield {
  compliance?: LiveYieldMetric;
  passed?: LiveYieldMetric;
  failedAndWarning?: LiveYieldMetric;
  totalRules?: LiveYieldMetric;
}

export interface ValidateFileResult {
  filename: string;
  docType: string;
  durationMs: number;
  summary: ValidateFileSummary;
  results: ValidateRuleResult[];
  resultsByCategory: Record<string, ValidateRuleResult[]>;
  summaryByCategory: Record<string, ValidateFileSummary>;
  sevenLayerValidation?: SevenLayerValidation;
  document?: ProcessedDocumentInfo;
  processingJob?: ProcessingJobInfo;
  pipelineStages?: ProcessingPipelineStage[];
  liveYield?: LiveYield;
}

/* Paged search helpers (internal) */

interface ListSearchPayload {
  keyword: string;
  sortByColumn?: string | null;
  sortingOrder?: "asc" | "desc" | "";
  filters?: Record<string, string | string[] | null | undefined>;
  draw?: number;
}

interface PagedSearchResult<T> {
  items: T[];
  totalItems: number;
  typeCounts?: Record<string, number>;

  draw: number;
  recordsTotal: number;
  recordsFiltered: number;
  rawData: unknown;
}

function pickListItems<T>(body: unknown): T[] {
  const seek = (v: unknown): T[] | null => {
    if (Array.isArray(v)) return v as T[];
    if (!v || typeof v !== "object") return null;
    const o = v as Record<string, unknown>;
    for (const k of [
      "content",
      "items",
      "records",
      "results",
      "catalogs",
      "profiles",
      "collections",
      "documents",
      "evidence",
      "data",
    ]) {
      const hit = seek(o[k]);
      if (hit) return hit;
    }
    return null;
  };
  return seek(body) ?? [];
}

const LIST_DATA_KEYS = [
  "content",
  "items",
  "records",
  "results",
  "catalogs",
  "profiles",
  "collections",
  "documents",
  "evidence",
  "data",
] as const;

function pickRawData(body: unknown): unknown {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== "object") return undefined;
  const o = body as Record<string, unknown>;
  for (const k of LIST_DATA_KEYS) {
    if (k in o) return o[k];
  }
  for (const k of ["data", "page", "result"]) {
    const nested = o[k];
    if (nested && typeof nested === "object") {
      const n = nested as Record<string, unknown>;
      for (const dk of LIST_DATA_KEYS) {
        if (dk in n) return n[dk];
      }
    }
  }
  return undefined;
}

function pickNumber(body: unknown, key: string): number | undefined {
  if (!body || typeof body !== "object") return undefined;
  const o = body as Record<string, unknown>;
  if (typeof o[key] === "number") return o[key] as number;
  for (const k of ["pagination", "page", "meta", "data"]) {
    const nested = o[k];
    if (nested && typeof nested === "object") {
      const v = (nested as Record<string, unknown>)[key];
      if (typeof v === "number") return v;
    }
  }
  return undefined;
}

function pickTotalCount(body: unknown, fallback: number): number {
  const keys = [
    "total",
    "totalItems",
    "totalRecords",
    "totalElements",
    "totalCount",
    "count",
  ];
  const seek = (v: unknown, depth: number): number | null => {
    if (!v || typeof v !== "object" || depth > 3) return null;
    const o = v as Record<string, unknown>;
    for (const k of keys) {
      if (typeof o[k] === "number") return o[k] as number;
    }
    for (const k of ["pagination", "page", "meta", "data"]) {
      const hit = seek(o[k], depth + 1);
      if (hit !== null) return hit;
    }
    return null;
  };
  return seek(body, 0) ?? fallback;
}

function pickTypeCounts(body: unknown): Record<string, number> | undefined {
  if (!body || typeof body !== "object") return undefined;
  const o = body as Record<string, unknown>;
  const raw = (o.typeCounts ??
    o.catalogTypeCounts ??
    o.statusCounts ??
    (o.data as Record<string, unknown> | undefined)?.typeCounts) as
    | Record<string, unknown>
    | undefined;
  if (!raw || typeof raw !== "object") return undefined;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v === "number") out[k.toUpperCase()] = v;
  }
  return Object.keys(out).length ? out : undefined;
}

function collectFilters(
  raw: Record<string, string | string[] | null | undefined> | undefined,
): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {};
  for (const [k, v] of Object.entries(raw ?? {})) {
    if (Array.isArray(v)) {
      if (v.length > 0) out[k] = v;
    } else if (v) {
      out[k] = v;
    }
  }
  return out;
}

async function postPagedSearch<T>(
  resourcePath: string,
  page: number,
  size: number,
  payload: ListSearchPayload,
): Promise<PagedSearchResult<T>> {
  const filters = collectFilters(payload.filters);
  const body = await apiClient.post<unknown>(`${resourcePath}/${page}/${size}`, {
    keyword: payload.keyword ?? "",
    sortByColumn: payload.sortByColumn ?? "",
    sortingOrder: payload.sortingOrder ?? "",
    ...(payload.draw !== undefined ? { draw: payload.draw } : {}),
    ...filters,
  });
  const items = pickListItems<T>(body);
  const totalItems = pickTotalCount(body, page * size + items.length);
  const recordsFiltered = pickNumber(body, "recordsFiltered") ?? totalItems;
  return {
    items,
    totalItems,
    typeCounts: pickTypeCounts(body),
    draw: pickNumber(body, "draw") ?? payload.draw ?? 0,
    recordsTotal: pickNumber(body, "recordsTotal") ?? totalItems,
    recordsFiltered,
    rawData: pickRawData(body),
  };
}

/* Supporting Evidence functions */

export function createSupportingEvidence(payload: {
  title: string;
  tags?: string[];
  description?: string;
  owningOrgUnitId?: string;
  requiresReview?: boolean;
  reviewCycleDays?: number;
  reviewAlertDays?: number;
}): Promise<ApiEnvelope<SupportingEvidence>> {
  return apiClient.post<ApiEnvelope<SupportingEvidence>>(
    `/content-registry/supporting-evidence`,
    payload,
  );
}

export function createSupportingEvidenceWithFile(payload: {
  title: string;
  file: File;
  tags?: string[];
  description?: string;
  owningOrgUnitId?: string;
}): Promise<ApiEnvelope<SupportingEvidence>> {
  const formData = new FormData();
  formData.append("file", payload.file);
  formData.append("title", payload.title);
  (payload.tags ?? []).forEach((tag) => formData.append("tags", tag));
  if (payload.description) formData.append("description", payload.description);
  if (payload.owningOrgUnitId) {
    formData.append("owningOrgUnitId", payload.owningOrgUnitId);
  }
  return apiClient.upload<ApiEnvelope<SupportingEvidence>>(
    `/content-registry/supporting-evidence`,
    formData,
  );
}

export function deleteSupportingEvidence(
  evidenceId: string,
): Promise<ApiEnvelope<null>> {
  return apiClient.delete<ApiEnvelope<null>>(
    `/content-registry/supporting-evidence/${evidenceId}`,
  );
}

export function listSupportingEvidence(): Promise<
  ApiEnvelope<SupportingEvidence[]>
> {
  return apiClient.get<ApiEnvelope<SupportingEvidence[]>>(
    `/content-registry/supporting-evidence`,
  );
}

export function searchSupportingEvidence(
  q: string,
): Promise<ApiEnvelope<SupportingEvidence[]>> {
  return apiClient.get<ApiEnvelope<SupportingEvidence[]>>(
    `/content-registry/supporting-evidence/search?q=${encodeURIComponent(q)}`,
  );
}

export function searchSupportingEvidencePaged(
  page: number,
  size: number,
  payload: ListSearchPayload,
): Promise<PagedSearchResult<SupportingEvidence>> {
  return postPagedSearch<SupportingEvidence>(
    "/content-registry/supporting-evidence",
    page,
    size,
    payload,
  );
}

export function getSupportingEvidence(
  id: string,
): Promise<ApiEnvelope<SupportingEvidenceResponse>> {
  return apiClient.get<ApiEnvelope<SupportingEvidenceResponse>>(
    `/content-registry/supporting-evidence/${id}`,
  );
}

export function updateSupportingEvidence(
  id: string,
  payload: { title?: string; description?: string; tags?: string[] },
): Promise<ApiEnvelope<SupportingEvidenceResponse>> {
  return apiClient.patch<ApiEnvelope<SupportingEvidenceResponse>>(
    `/content-registry/supporting-evidence/${id}`,
    payload,
  );
}

export function uploadSupportingEvidenceFile(
  id: string,
  file: File,
): Promise<ApiEnvelope<SupportingEvidenceResponse>> {
  const formData = new FormData();
  formData.append("file", file);
  return apiClient.upload<ApiEnvelope<SupportingEvidenceResponse>>(
    `/content-registry/supporting-evidence/${id}/upload`,
    formData,
  );
}

export function downloadSupportingEvidenceFile(id: string): string {
  return apiClient.buildUrl(
    `/content-registry/supporting-evidence/${id}/download`,
  );
}

export function previewSupportingEvidenceFile(id: string): string {
  return apiClient.buildUrl(
    `/content-registry/supporting-evidence/${id}/preview`,
  );
}

export function searchEvidenceTags(
  prefix: string,
): Promise<ApiEnvelope<string[]>> {
  return apiClient.get<ApiEnvelope<string[]>>(
    `/content-registry/supporting-evidence/tags/search?prefix=${encodeURIComponent(prefix)}`,
  );
}

export function uploadEvidenceFile(
  evidenceId: string,
  file: File,
): Promise<ApiEnvelope<SupportingEvidence>> {
  const formData = new FormData();
  formData.append("file", file);
  return apiClient.upload<ApiEnvelope<SupportingEvidence>>(
    `/content-registry/supporting-evidence/${evidenceId}/upload`,
    formData,
  );
}

const _EVIDENCE_IMAGE_EXTS = ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "ico"];
const _EVIDENCE_TEXT_EXTS = [
  "yaml", "yml", "json", "xml", "csv", "txt", "md", "log",
  "html", "htm", "ini", "conf", "sh", "js", "ts", "css",
];
const _EVIDENCE_BINARY_EXTS = [
  "xlsx", "xls", "docx", "doc", "pptx", "ppt",
  "zip", "rar", "7z", "tar", "gz", "bin", "exe",
];

export async function previewEvidenceFile(
  evidenceId: string,
): Promise<EvidencePreview> {
  const res = await fetch(
    apiClient.buildUrl(
      `/content-registry/supporting-evidence/${evidenceId}/preview`,
    ),
    {
      credentials: "include",
    },
  );
  if (!res.ok) {
    throw new Error(`Preview failed: ${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  const type = (blob.type || "").toLowerCase();

  const disposition = res.headers.get("content-disposition") || "";
  let fileName = "";
  const nameMatch = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  if (nameMatch) {
    const raw = nameMatch[1].trim();
    try {
      fileName = decodeURIComponent(raw);
    } catch {
      fileName = raw;
    }
  }
  const ext = (fileName.split(".").pop() || "").toLowerCase();

  if (blob.size === 0) {
    return { url: URL.createObjectURL(blob), previewable: false, fileName };
  }

  if (type === "application/pdf" || ext === "pdf") {
    return { url: URL.createObjectURL(blob), previewable: true, fileName };
  }
  if (type.startsWith("image/") || _EVIDENCE_IMAGE_EXTS.includes(ext)) {
    return { url: URL.createObjectURL(blob), previewable: true, fileName };
  }

  const looksBinaryMime =
    type.includes("officedocument") ||
    type.includes("ms-excel") ||
    type.includes("ms-powerpoint") ||
    type.includes("msword") ||
    type.includes("zip") ||
    type.includes("compressed");
  if (_EVIDENCE_BINARY_EXTS.includes(ext) || looksBinaryMime) {
    return { url: URL.createObjectURL(blob), previewable: false, fileName };
  }

  const looksText =
    type.startsWith("text/") ||
    type.includes("json") ||
    type.includes("xml") ||
    type.includes("yaml") ||
    type === "application/octet-stream" ||
    type === "" ||
    _EVIDENCE_TEXT_EXTS.includes(ext);
  if (looksText) {
    try {
      const textBlob = new Blob([await blob.arrayBuffer()], {
        type: "text/plain",
      });
      return {
        url: URL.createObjectURL(textBlob),
        previewable: true,
        fileName,
      };
    } catch {
      return { url: URL.createObjectURL(blob), previewable: true, fileName };
    }
  }

  return { url: URL.createObjectURL(blob), previewable: true, fileName };
}

export function linkEvidenceToStatement(
  planId: string,
  payload: { statementId: string; evidenceIds: string[] },
): Promise<ApiEnvelope<EvidenceLink[]>> {
  return apiClient.post<ApiEnvelope<EvidenceLink[]>>(
    `/assessment-execution/${planId}/evidence`,
    payload,
  );
}

export function getLinkedEvidence(
  planId: string,
  moduleName?: string,
): Promise<ApiEnvelope<EvidenceLink[]>> {
  const url = moduleName
    ? `/assessment-execution/${planId}/evidence?moduleName=${moduleName}`
    : `/assessment-execution/${planId}/evidence`;
  return apiClient.get<ApiEnvelope<EvidenceLink[]>>(url);
}

export function unlinkEvidence(
  planId: string,
  evidenceId: string,
  statementId: string,
): Promise<ApiEnvelope<null>> {
  return apiClient.delete<ApiEnvelope<null>>(
    `/assessment-execution/${planId}/evidence/${evidenceId}?statementId=${encodeURIComponent(statementId)}`,
  );
}

/* Governance Document functions */

export function listGovernanceDocuments(params?: {
  type?: GovernanceDocumentType;
  status?: DocumentLifecycleStatus;
}): Promise<ApiEnvelope<GovernanceDocumentSummaryResponse[]>> {
  const qs = new URLSearchParams();
  if (params?.type) qs.set("type", params.type);
  if (params?.status) qs.set("status", params.status);
  const q = qs.toString();
  return apiClient.get<ApiEnvelope<GovernanceDocumentSummaryResponse[]>>(
    `/content-registry/governance-documents${q ? `?${q}` : ""}`,
  );
}

export function getGovernanceReviewSummary(
  type?: GovernanceDocumentType,
): Promise<ApiEnvelope<ReviewSummaryResponse>> {
  const path = type
    ? `/content-registry/governance-documents/review-summary?type=${type}`
    : `/content-registry/governance-documents/review-summary`;
  return apiClient.get<ApiEnvelope<ReviewSummaryResponse>>(path);
}

export function getGovernanceDocument(
  id: string,
): Promise<ApiEnvelope<GovernanceDocumentResponse>> {
  return apiClient.get<ApiEnvelope<GovernanceDocumentResponse>>(
    `/content-registry/governance-documents/${id}`,
  );
}

export function createGovernanceDocument(payload: {
  documentType: GovernanceDocumentType;
  title: string;
  description?: string;
  owningOrgUnitId?: string;
  requiresReview?: boolean;
  reviewCycleDays?: number;
  reviewAlertDays?: number;
}): Promise<ApiEnvelope<GovernanceDocumentResponse>> {
  return apiClient.post<ApiEnvelope<GovernanceDocumentResponse>>(
    `/content-registry/governance-documents`,
    payload,
  );
}

export function updateGovernanceDocument(
  id: string,
  payload: {
    title?: string;
    description?: string;
    owningOrgUnitId?: string;
    requiresReview?: boolean;
    reviewCycleDays?: number;
    reviewAlertDays?: number;
  },
): Promise<ApiEnvelope<GovernanceDocumentResponse>> {
  return apiClient.patch<ApiEnvelope<GovernanceDocumentResponse>>(
    `/content-registry/governance-documents/${id}`,
    payload,
  );
}

export function deleteGovernanceDocument(
  id: string,
): Promise<{ success: boolean }> {
  return apiClient.delete<{ success: boolean }>(
    `/content-registry/governance-documents/${id}`,
  );
}

export function deleteGovernanceDocumentOrphan(id: string): void {
  try {
    void fetch(
      apiClient.buildUrl(`/content-registry/governance-documents/${id}`),
      {
        method: "DELETE",
        credentials: "include",
        keepalive: true,
      },
    ).catch(() => { });
  } catch {
  }
}

export function uploadGovernanceDocumentFile(
  file: File,
  options: { orgId?: string; domainName?: string } = {},
): Promise<ApiEnvelope<DocEngineIngestUploadResponse>> {
  const formData = new FormData();
  formData.append("file", file);

  // `domainName` is the doc-engine's domain ("SCRG") — the ingest service
  // stores it with the document, like orgId.
  const query = new URLSearchParams();
  if (options.orgId) query.set("orgId", options.orgId);
  if (options.domainName) query.set("domainName", options.domainName);
  const qs = query.toString();

  return apiClient.upload<ApiEnvelope<DocEngineIngestUploadResponse>>(
    `/content-registry/docengine/ingest/upload${qs ? `?${qs}` : ""}`,
    formData,
    { envelopeError: true },
  );
}

export function deleteGovernanceDocumentFile(
  id: string,
): Promise<ApiEnvelope<GovernanceDocumentResponse>> {
  return apiClient.delete<ApiEnvelope<GovernanceDocumentResponse>>(
    `/content-registry/governance-documents/${id}/file`,
  );
}

export function downloadGovernanceDocumentFile(id: string): string {
  return apiClient.buildUrl(
    `/content-registry/governance-documents/${id}/download`,
  );
}

export function previewGovernanceDocumentFile(id: string): string {
  return apiClient.buildUrl(
    `/content-registry/governance-documents/${id}/preview`,
  );
}

export function listGovernanceDocumentVersions(
  id: string,
): Promise<ApiEnvelope<VersionInfo[]>> {
  return apiClient.get<ApiEnvelope<VersionInfo[]>>(
    `/content-registry/governance-documents/${id}/versions`,
  );
}

export function downloadGovernanceDocumentVersionFile(
  id: string,
  versionNumber: number,
): string {
  return apiClient.buildUrl(
    `/content-registry/governance-documents/${id}/versions/${versionNumber}/download`,
  );
}

export function createNewGovernanceDocumentVersion(
  id: string,
): Promise<ApiEnvelope<GovernanceDocumentResponse>> {
  return apiClient.post<ApiEnvelope<GovernanceDocumentResponse>>(
    `/content-registry/governance-documents/${id}/new-version`,
  );
}

export function submitDocEngineDocumentForReview(
  documentId: string,
): Promise<ApiEnvelope<GovernanceDocumentResponse>> {
  return apiClient.post<ApiEnvelope<GovernanceDocumentResponse>>(
    `/content-registry/docengine/ingest/organizations/${encodeURIComponent(
      documentId,
    )}/submit`,
  );
}

export function reviewDocEngineDocument(
  documentId: string,
  action: "APPROVE" | "REJECT" | "PUBLISH",
  reason?: string,
): Promise<ApiEnvelope<GovernanceDocumentResponse>> {
  const body: { action: string; reason?: string } = { action };
  if (action === "REJECT" && reason) {
    body.reason = reason;
  }
  return apiClient.post<ApiEnvelope<GovernanceDocumentResponse>>(
    `/content-registry/docengine/ingest/organizations/${encodeURIComponent(
      documentId,
    )}/review`,
    body,
  );
}

export function retireDocEngineDocument(
  documentId: string,
  reason: string,
): Promise<ApiEnvelope<GovernanceDocumentResponse>> {
  return apiClient.post<ApiEnvelope<GovernanceDocumentResponse>>(
    `/content-registry/docengine/ingest/organizations/${encodeURIComponent(
      documentId,
    )}/retire`,
    { reason },
  );
}

export function getGovernanceDocumentHistory(
  id: string,
  params?: { eventType?: string },
): Promise<ApiEnvelope<HistoryEvent[]>> {
  const qs = new URLSearchParams();
  if (params?.eventType) qs.set("eventType", params.eventType);
  const q = qs.toString();
  return apiClient.get<ApiEnvelope<HistoryEvent[]>>(
    `/content-registry/governance-documents/${id}/history${q ? `?${q}` : ""}`,
  );
}

export function getDocEngineDocumentHistory(
  documentId: string,
  params?: { eventType?: string; page?: number; pageSize?: number },
): Promise<ApiEnvelope<HistoryEvent[]>> {
  const qs = new URLSearchParams();
  if (params?.eventType) qs.set("eventType", params.eventType);
  if (params?.page !== undefined) qs.set("page", String(params.page));
  if (params?.pageSize !== undefined) qs.set("pageSize", String(params.pageSize));
  const q = qs.toString();
  return apiClient.get<ApiEnvelope<HistoryEvent[]>>(
    `/content-registry/docengine/ingest/organizations/${encodeURIComponent(
      documentId,
    )}/history${q ? `?${q}` : ""}`,
  );
}

/* Doc-Engine ingest / list functions */

export async function subscribeDocEngineIngestEvents(
  documentId: string,
  { onEvent, signal }: DocEngineIngestSubscribeOptions,
): Promise<void> {
  await apiClient.subscribeSSE(
    `/content-registry/docengine/ingest/documents/${documentId}/events`,
    {
      signal,
      onMessage: (message) => {
        let payload: DocEngineIngestEvent = {};
        if (message.data) {
          try {
            const parsed: unknown = JSON.parse(message.data);
            payload = parsed && typeof parsed === "object"
              ? parsed as DocEngineIngestEvent
              : { message: String(parsed) };
          } catch {
            payload = { message: message.data };
          }
        }
        onEvent(message.event, payload);
      },
    },
  );
}

/** Server-side paged list of ingested documents. */
export async function listDocEngineDocuments(
  page: number,
  pageSize: number,
  payload: DocEngineListPayload = {},
): Promise<DocEngineListResult> {
  const body: Record<string, unknown> = {
    trashed: payload.trashed ?? false,
    keyword: payload.keyword ?? "",
    domainName: payload.domainName ?? "",
    documentType: payload.documentType ?? "",
    organizationId: payload.organizationId ?? "",
  };
  if (payload.docStatus) body.docStatus = payload.docStatus;

  const response = await apiClient.post<DocEngineDocumentListResponse>(
    `/content-registry/docengine/ingest/list/${page}/${pageSize}`,
    body,
  );
  const items = Array.isArray(response?.data) ? response!.data! : [];
  const pg = response?.pagination;
  return {
    items,
    totalItems: pg?.totalCount ?? items.length,
    page: pg?.page ?? page,
    pageSize: pg?.pageSize ?? pageSize,
    totalPages: pg?.totalPages ?? 1,
  };
}

/** Returns document totals grouped by SCF domain for the registry overview. */
export async function getDomainDocumentCounts(
  organizationId: string,
): Promise<DomainDocumentCount[]> {
  const response = await apiClient.get<{
    status?: string;
    data?: DomainDocumentCount[];
  }>(
    `/content-registry/docengine/ingest/organizations/${encodeURIComponent(organizationId)}/domains/documentCount`,
  );
  return Array.isArray(response?.data) ? response.data : [];
}

export async function saveDocumentIngest(
  payload: SaveDocumentIngestPayload,
): Promise<unknown> {
  return apiClient.post<unknown>(
    "/content-registry/docengine/ingest/organizations",
    payload,
  );
}

/** Delete an ingested document. `permanent` removes it from the database. */
export async function deleteDocEngineDocument(
  documentId: string,
  { permanent = true }: { permanent?: boolean } = {},
): Promise<unknown> {
  return apiClient.delete<unknown>(
    `/content-registry/docengine/ingest/documents/${documentId}?permanent=${permanent}`,
  );
}

/** Direct URL of the stored file for in-app preview (`<FilePreview>` fetches
 *  it with the bearer token). Does not perform a request itself. */
export function previewDocEngineDocumentFile(documentId: string): string {
  return apiClient.buildUrl(
    `/content-registry/docengine/ingest/documents/${encodeURIComponent(documentId)}/preview`,
  );
}

/** Direct URL of the stored file for download — pass it to `triggerDownload`,
 *  which fetches it with the bearer token and saves it. */
export function downloadDocEngineDocumentFile(documentId: string): string {
  return apiClient.buildUrl(
    `/content-registry/docengine/ingest/documents/${encodeURIComponent(documentId)}/download`,
  );
}

/* Classifier fetchers (domain / concept / requirement / consistency /
   quality / governance)

   Single backend endpoint — the layer name is sent in the POST body.
   Callers invoke each layer one at a time, in the order they need. */

export type ClassifyLayerName =
  | "domain"
  | "concept"
  | "requirement"
  | "relationship"
  | "consistency"
  | "knowledge_quality"
  | "governance";

function unwrapClassifyResult<T>(raw: unknown): T {
  if (raw && typeof raw === "object") {
    const wrapped = raw as { data?: T; result?: T };
    if (wrapped.data && typeof wrapped.data === "object") return wrapped.data;
    if (wrapped.result && typeof wrapped.result === "object") {
      return wrapped.result;
    }
  }
  return (raw as T) ?? ({} as T);
}

async function postClassifyLayer<T>(
  documentId: string,
  layer: ClassifyLayerName,
  signal?: AbortSignal,
): Promise<T> {
  const raw = await apiClient.post<unknown>(
    `/content-registry/docengine/classify/layers/${documentId}`,
    { layer },
    { signal },
  );
  return unwrapClassifyResult<T>(raw);
}

export async function getDomainClassifyResult(
  documentId: string,
  signal?: AbortSignal,
): Promise<DomainClassifyResult> {
  const raw = await apiClient.get<unknown>(
    `/content-registry/docengine/classify/domains/${documentId}`,
    { signal },
  );
  return unwrapClassifyResult<DomainClassifyResult>(raw);
}

export function getDomainRuleValidation(
  documentId: string,
  scopeKey: string,
  layer: string,
  outcome: string,
  signal?: AbortSignal,
): Promise<DomainRuleValidation[]> {
  const query = new URLSearchParams({ scopeKey, layer, outcome });
  return apiClient.get<DomainRuleValidation[]>(
    `/content-registry/docengine/ingest/documents/${encodeURIComponent(documentId)}/findings?${query.toString()}`,
    { signal },
  );
}

/** Catalog entry behind a semantic validation rule (name, scope, exemplars). */
export interface SemanticValidationRule {
  id?: string;
  ruleKey: string;
  layer?: string;
  scopeKey?: string;
  scopeName?: string;
  ruleName: string;
  ruleExplanation?: string;
  severity?: string;
  validationType?: string;
  enabled?: boolean;
  version?: number;
  examplePass?: string;
  exampleFail?: string;
}

/** Rule catalog for a layer + scope, e.g. domain/SCRG. */
export function getSemanticValidationRules(
  layer: string,
  scopeKey: string,
  signal?: AbortSignal,
): Promise<ApiEnvelope<SemanticValidationRule[]>> {
  return apiClient.get<ApiEnvelope<SemanticValidationRule[]>>(
    `/content-registry/semanticValidation/rules/byLayer/${encodeURIComponent(layer)}/scope/${encodeURIComponent(scopeKey)}`,
    { signal },
  );
}

export function getConceptClassifyResult(
  documentId: string,
  signal?: AbortSignal,
): Promise<ConceptClassifyResult> {
  return postClassifyLayer<ConceptClassifyResult>(documentId, "concept", signal);
}

export function getRequirementClassifyResult(
  documentId: string,
  signal?: AbortSignal,
): Promise<RequirementClassifyResult> {
  return postClassifyLayer<RequirementClassifyResult>(
    documentId,
    "requirement",
    signal,
  );
}

export function getRelationshipClassifyResult(
  documentId: string,
  signal?: AbortSignal,
): Promise<unknown> {
  return postClassifyLayer<unknown>(documentId, "relationship", signal);
}

export function getConsistencyClassifyResult(
  documentId: string,
  signal?: AbortSignal,
): Promise<ConsistencyResult> {
  return postClassifyLayer<ConsistencyResult>(documentId, "consistency", signal);
}

export function getKnowledgeQualityClassifyResult(
  documentId: string,
  signal?: AbortSignal,
): Promise<KnowledgeQualityResult> {
  return postClassifyLayer<KnowledgeQualityResult>(documentId, "knowledge_quality", signal);
}

export function getGovernanceClassifyResult(
  documentId: string,
  signal?: AbortSignal,
): Promise<GovernanceResult> {
  return postClassifyLayer<GovernanceResult>(documentId, "governance", signal);
}

/* Semantic validation merge helpers */

interface SemanticValidationRuleEntry {
  ruleId?: string;
  code?: string;
  name?: string;
  severity?: string;
  status?: string;
  findings?: number;
  summary?: string;
}

interface SemanticValidationResponse {
  data?: {
    documentId?: string;
    status?: string;
    summary?: {
      rules?: number;
      passed?: number;
      failed?: number;
      warnings?: number;
      notApplicable?: number;
      inconclusive?: number;
      findings?: number;
      passedRules?: SemanticValidationRuleEntry[];
      failedRules?: SemanticValidationRuleEntry[];
      warningRules?: SemanticValidationRuleEntry[];
      inconclusiveRules?: SemanticValidationRuleEntry[];
      notApplicableRules?: SemanticValidationRuleEntry[];
    };
    score?: {
      percentage?: number;
    };
    confidence?: {
      value?: number;
      band?: string;
      byEvidence?: Record<string, number>;
    };
    findings?: Array<{
      ruleId?: string;
      title?: string;
      severity?: string;
    }>;
  };
}

const SEMANTIC_LAYER_CLEARED_THRESHOLD = 0.5;

const SEMANTIC_LAYER_KEY_ALIAS: Record<string, string> = {
  KNOWLEDGE: "quality",
};

const RULE_PREFIX_TO_CATEGORY: Record<string, string> = {
  CON: "concept",
  REL: "relationship",
  GOV: "governance",
  REQ: "requirement",
  KQ: "quality",
  DOM: "domain",
  CNS: "consistency",
};

function categoryFromRuleCode(code: string | undefined): string {
  const prefix = (code ?? "").split("-")[0]?.toUpperCase();
  return RULE_PREFIX_TO_CATEGORY[prefix] ?? "semantic";
}

export function mergeSemanticValidation(
  base: ValidateFileResult,
  raw: SemanticValidationResponse | null | undefined,
): ValidateFileResult {
  const data = raw?.data;
  if (!data) return base;

  const findingByRule = new Map<string, string>();
  for (const f of data.findings ?? []) {
    if (f.ruleId && f.title) findingByRule.set(f.ruleId, f.title);
  }

  const toRule = (
    r: SemanticValidationRuleEntry,
    status: ValidateRuleStatus,
    skipReason?: string,
  ): ValidateRuleResult => {
    const ruleId = r.ruleId ?? r.code ?? "";
    return {
      ruleId,
      name: r.name ?? r.code ?? ruleId,
      severity: status === "FAIL" ? "ERROR" : "WARN",
      status,
      evidence: findingByRule.get(ruleId) || r.summary || undefined,
      category: categoryFromRuleCode(ruleId),
      ...(skipReason ? { skipReason } : {}),
    };
  };

  const s = data.summary ?? {};
  const semanticRules: ValidateRuleResult[] = [
    ...(s.passedRules ?? []).map((r) => toRule(r, "PASS")),
    ...(s.failedRules ?? []).map((r) => toRule(r, "FAIL")),
    ...(s.warningRules ?? []).map((r) => toRule(r, "WARN")),
    ...(s.inconclusiveRules ?? []).map((r) =>
      toRule(r, "SKIPPED", "Inconclusive"),
    ),
    ...(s.notApplicableRules ?? []).map((r) =>
      toRule(r, "SKIPPED", "Not applicable to this document"),
    ),
  ];

  const seen = new Set(base.results.map((r) => r.ruleId));
  const merged = [
    ...base.results,
    ...semanticRules.filter((r) => r.ruleId && !seen.has(r.ruleId)),
  ];

  const resultsByCategory: Record<string, ValidateRuleResult[]> = {};
  for (const r of merged) {
    (resultsByCategory[r.category] ??= []).push(r);
  }

  const countSummary = (rules: ValidateRuleResult[]): ValidateFileSummary => {
    const out: ValidateFileSummary = {
      pass: 0,
      fail: 0,
      warn: 0,
      skipped: 0,
      total: rules.length,
    };
    for (const r of rules) {
      if (r.status === "PASS") out.pass++;
      else if (r.status === "FAIL") out.fail++;
      else if (r.status === "WARN") out.warn++;
      else out.skipped++;
    }
    return out;
  };

  const summary = countSummary(merged);
  const summaryByCategory: Record<string, ValidateFileSummary> = {};
  for (const [cat, rules] of Object.entries(resultsByCategory)) {
    summaryByCategory[cat] = countSummary(rules);
  }

  const byEvidence = data.confidence?.byEvidence ?? {};
  const scoredKeys = Object.keys(byEvidence);
  let sevenLayerValidation = base.sevenLayerValidation;
  if (scoredKeys.length > 0) {
    const existingByKey = new Map<string, SevenLayerValidationLayer>();
    for (const l of base.sevenLayerValidation?.layers ?? []) {
      existingByKey.set(l.key, l);
    }
    for (const rawKey of scoredKeys) {
      const score = byEvidence[rawKey] ?? 0;
      const key =
        SEMANTIC_LAYER_KEY_ALIAS[rawKey] ?? rawKey.toLowerCase();
      const passed = score >= SEMANTIC_LAYER_CLEARED_THRESHOLD;
      const prior = existingByKey.get(key);
      existingByKey.set(key, {
        key,
        label: prior?.label ?? rawKey,
        status: passed ? "cleared" : "not_cleared",
        passed,
        percentage: Math.round(score * 100),
        formattedPercentage: `${Math.round(score * 100)}%`,
        score,
        rulesChecked: prior?.rulesChecked ?? 0,
        rulesPassed: prior?.rulesPassed ?? 0,
        hint: prior?.hint,
      });
    }
    const layers = Array.from(existingByKey.values());
    const clearedLayers = layers.filter((l) => l.passed).length;
    sevenLayerValidation = {
      totalLayers: base.sevenLayerValidation?.totalLayers ?? layers.length,
      clearedLayers,
      status:
        clearedLayers === layers.length
          ? "cleared"
          : clearedLayers === 0
            ? "not_cleared"
            : "partial",
      crossCuttingRules: base.sevenLayerValidation?.crossCuttingRules,
      layers,
    };
  }

  const scorePct = data.score?.percentage;
  const compliancePercentage =
    typeof scorePct === "number"
      ? scorePct
      : summary.total > 0
        ? Math.round((summary.pass / summary.total) * 100)
        : 0;
  const liveYield: LiveYield = {
    totalRules: {
      count: summary.total,
      label: "total rules checked",
      status: "complete",
    },
    passed: {
      count: summary.pass,
      label: "rules passed",
      status: summary.pass > 0 ? "active" : "complete",
    },
    failedAndWarning: {
      failedCount: summary.fail,
      warningCount: summary.warn,
      totalCount: summary.fail + summary.warn,
      label: "failed + warning",
      status: summary.fail + summary.warn > 0 ? "attention" : "complete",
    },
    compliance: {
      percentage: compliancePercentage,
      score: compliancePercentage / 100,
      label: "compliance score",
      status: compliancePercentage >= 80 ? "complete" : "attention",
    },
  };

  return {
    ...base,
    results: merged,
    resultsByCategory,
    summary,
    summaryByCategory,
    sevenLayerValidation,
    liveYield,
  };
}

export function buildValidateResultFromSemantic(
  raw: SemanticValidationResponse | null | undefined,
  meta: { filename?: string; docType?: string; durationMs?: number } = {},
): ValidateFileResult {
  const emptyBase: ValidateFileResult = {
    filename: meta.filename ?? "",
    docType: meta.docType ?? "",
    durationMs: meta.durationMs ?? 0,
    summary: { pass: 0, fail: 0, warn: 0, skipped: 0, total: 0 },
    results: [],
    resultsByCategory: {},
    summaryByCategory: {},
  };
  return mergeSemanticValidation(emptyBase, raw);
}
